import React from 'react';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { summaryMetrics, monthlyEmissionsData, reportChecklist } from '../data/mock';

export default function Dashboard() {
  const chartOptions = {
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.9)', borderColor: '#334155', textStyle: { color: '#f8fafc' } },
    legend: { data: ['Scope 1', 'Scope 2', 'Scope 3'], textStyle: { color: '#94a3b8' }, bottom: 0 },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: monthlyEmissionsData.months, axisLabel: { color: '#64748b' }, axisLine: { lineStyle: { color: '#334155' } } },
    yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } } },
    series: [
      { name: 'Scope 1', type: 'line', stack: 'Total', smooth: true, lineStyle: { width: 0 }, showSymbol: false, areaStyle: { opacity: 0.8, color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#10b981' }, { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }] } }, data: monthlyEmissionsData.scope1 },
      { name: 'Scope 2', type: 'line', stack: 'Total', smooth: true, lineStyle: { width: 0 }, showSymbol: false, areaStyle: { opacity: 0.8, color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#06b6d4' }, { offset: 1, color: 'rgba(6, 182, 212, 0.1)' }] } }, data: monthlyEmissionsData.scope2 },
      { name: 'Scope 3', type: 'line', stack: 'Total', smooth: true, lineStyle: { width: 0 }, showSymbol: false, areaStyle: { opacity: 0.8, color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#3b82f6' }, { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }] } }, data: monthlyEmissionsData.scope3 }
    ]
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Emissions Overview</h1>
          <p className="text-slate-400 text-sm mt-1">Real-time analytical view of your corporate carbon footprint.</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 text-sm font-medium text-slate-300 glass-panel rounded-lg hover:text-white glass-panel-hover flex items-center gap-2">
            <SafeIcon name="Download" className="w-4 h-4" /> Export Data
          </button>
          <button className="px-4 py-2 text-sm font-medium text-slate-900 bg-emerald-500 rounded-lg hover:bg-emerald-400 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.3)] flex items-center gap-2">
            <SafeIcon name="Plus" className="w-4 h-4" /> Add Record
          </button>
        </div>
      </div>

      {/* Top Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-panel p-5 rounded-xl border-l-4 border-l-emerald-500 relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl"></div>
          <p className="text-slate-400 text-sm font-medium mb-1">Total Emissions (YTD)</p>
          <div className="flex items-baseline gap-2">
            <h2 className="text-3xl font-bold text-slate-50 font-mono tracking-tight">{summaryMetrics.totalEmissions.toLocaleString()}</h2>
            <span className="text-slate-500 text-sm">{summaryMetrics.unit}</span>
          </div>
          <div className="mt-3 flex items-center gap-1 text-emerald-400 text-sm font-medium">
            <SafeIcon name="TrendingDown" className="w-4 h-4" />
            <span>{Math.abs(summaryMetrics.trend)}% vs last year</span>
          </div>
        </div>

        {summaryMetrics.scopes.map(scope => (
          <div key={scope.id} className="glass-panel p-5 rounded-xl glass-panel-hover group cursor-default">
            <div className="flex justify-between items-start mb-1">
              <p className="text-slate-400 text-sm font-medium">{scope.name}</p>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: scope.color }}></div>
            </div>
            <div className="flex items-baseline gap-2">
              <h2 className="text-2xl font-bold text-slate-100 font-mono">{scope.value.toLocaleString()}</h2>
            </div>
            <div className="mt-3 flex items-center gap-1 text-emerald-400 text-xs">
              <SafeIcon name="ArrowDownRight" className="w-3 h-3" />
              <span>{Math.abs(scope.trend)}% reduction</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Chart & Side Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-panel p-6 rounded-xl">
          <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
            <SafeIcon name="Activity" className="text-cyan-400" /> Emissions Trajectory
          </h3>
          <div className="h-[350px] w-full">
            <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        <div className="space-y-6">
          {/* Checklist */}
          <div className="glass-panel p-6 rounded-xl flex-1">
            <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
              <SafeIcon name="CheckSquare" className="text-emerald-400" /> Audit Readiness
            </h3>
            <div className="space-y-3">
              {reportChecklist.map(item => (
                <div key={item.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors">
                  <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${item.completed ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'border-slate-600 text-transparent'}`}>
                    {item.completed && <SafeIcon name="Check" className="w-3 h-3" />}
                  </div>
                  <span className={`text-sm ${item.completed ? 'text-slate-400 line-through' : 'text-slate-200'}`}>{item.task}</span>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors">
              View Action Plan
            </button>
          </div>

          {/* Quick Stats */}
          <div className="glass-panel p-6 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800/80">
            <h3 className="text-sm font-semibold text-slate-400 mb-4 uppercase tracking-wider">Data Quality Index</h3>
            <div className="flex items-end gap-4">
              <div className="text-4xl font-bold text-cyan-400 font-mono">92<span className="text-lg text-slate-500">%</span></div>
              <div className="pb-1 text-sm text-slate-400">High confidence</div>
            </div>
            <div className="w-full bg-slate-800 h-2 rounded-full mt-4 overflow-hidden">
              <div className="bg-gradient-to-r from-cyan-500 to-emerald-400 h-full rounded-full" style={{ width: '92%' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}